import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CalendarDays, Users, IndianRupee, Sparkles, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { Link } from "react-router-dom";
import { norm } from "@/lib/normalize";
import { AreaChart, Area } from "recharts";
import { formatINR, formatINRCompact } from "@/lib/currency";
import { formatDate, formatClock } from "@/lib/datetime";
import {
  Search, Bell, MoreHorizontal, ArrowUpRight, ArrowDownRight, ArrowUp,
  Scissors, Palette as SparkIcon, Package, Star, Store,
} from "lucide-react";

const money = (n) => formatINR(n);
const compact = (n) => formatINRCompact(n);

const CATEGORY_ICON = {
  haircut: { icon: Scissors, bg: "#E4F4CE", fg: "#3B5A22" },
  hair: { icon: Scissors, bg: "#E4F4CE", fg: "#3B5A22" },
  color: { icon: SparkIcon, bg: "#FFE0E9", fg: "#B0325D" },
  coloring: { icon: SparkIcon, bg: "#FFE0E9", fg: "#B0325D" },
  treatment: { icon: SparkIcon, bg: "#DBE6FF", fg: "#3F5C9F" },
  spa: { icon: SparkIcon, bg: "#DBE6FF", fg: "#3F5C9F" },
  facial: { icon: SparkIcon, bg: "#DBE6FF", fg: "#3F5C9F" },
  nails: { icon: SparkIcon, bg: "#FFE0E9", fg: "#B0325D" },
  nail: { icon: SparkIcon, bg: "#FFE0E9", fg: "#B0325D" },
  massage: { icon: SparkIcon, bg: "#FFECC7", fg: "#7A5A1A" },
  service: { icon: SparkIcon, bg: "#F3ECFF", fg: "#6A4EB0" },
};
const svcIcon = (cat) => CATEGORY_ICON[(cat || "service").toLowerCase()] || CATEGORY_ICON.service;

const SCHEDULE_CLASS = {
  haircut: "schedule-haircut",
  hair: "schedule-haircut",
  color: "schedule-coloring",
  coloring: "schedule-coloring",
  treatment: "schedule-treatment",
  spa: "schedule-treatment",
  facial: "schedule-treatment",
  nail: "schedule-nail",
  nails: "schedule-nail",
  massage: "schedule-massage",
  service: "schedule-treatment",
};
const scheduleClass = (cat) => SCHEDULE_CLASS[(cat || "service").toLowerCase()] || SCHEDULE_CLASS.service;

const initials = (name) =>
  (name || "").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

// ----- Customer view (unchanged aesthetic) -----
function CustomerView({ user, appts }) {
  const upcoming = appts.filter((a) => a.status === "scheduled").slice(0, 5);
  return (
    <div className="space-y-10" data-testid="customer-overview">
      <div>
        <div className="overline mb-3">Welcome back</div>
        <h1 className="font-heading text-5xl text-white">Hello, {(user.name || user.firstName || "").split(" ")[0]}.</h1>
        <p className="text-emerald-muted mt-3">Ready for your next ritual?</p>
        <Link to="/dashboard/book" data-testid="cta-book-appointment" className="gold-button inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full">
          Book Appointment <SparkIcon className="w-4 h-4" />
        </Link>
      </div>
      <div>
        <h2 className="font-heading text-2xl text-white mb-4">Upcoming</h2>
        <div className="space-y-3">
          {upcoming.length === 0 && <p className="text-emerald-muted text-sm">No upcoming appointments yet.</p>}
          {upcoming.map((a) => (
            <div key={a.id} className="bg-emerald-surface border border-gold-subtle rounded-xl p-5 flex justify-between items-center">
              <div>
                <div className="font-heading text-xl text-white">{a.service?.name}</div>
                <div className="text-xs text-emerald-muted mt-1">with {a.staff?.name} · {formatDate(a.date)} at {formatClock(a.time)}</div>
              </div>
              <div className="text-gold">${a.price}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ----- KPI mini card -----
function Kpi({ label, value, delta, icon: Icon }) {
  const isDown = delta && delta.startsWith("-");
  return (
    <div className="kpi-card p-5 flex-1 min-w-[170px]" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-xs text-[#8B8680] font-medium">{label}</div>
      <div className="flex items-end justify-between mt-3">
        <div className="flex items-center gap-2">
          <div className="text-3xl font-semibold text-[#1F1B1A] tracking-tight">{value}</div>
          <Icon className="w-4 h-4 text-[#B7B0A9] mb-1" />
        </div>
        {delta && (
          <div className={`text-xs font-medium flex items-center gap-0.5 ${isDown ? "text-red-500" : "text-emerald-600"}`}>
            {isDown ? <ArrowDownRight className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />}
            {delta.replace("-", "")}
          </div>
        )}
      </div>
    </div>
  );
}

// ----- Admin/Staff Light Dashboard -----
function AdminDash({ user, data, selectedMonth, onMonthChange }) {
  const kpi = data.kpis || {};
  const rev = data.revenue || {};
  const chartData = (rev.series || []).map((r, i) => ({
    day: r.day,
    revenue: r.revenue,
    expenses: r.expenses || r.expense || ((rev.expensesSeries && rev.expensesSeries[i]) ? rev.expensesSeries[i].expenses || rev.expensesSeries[i].expense || rev.expensesSeries[i].amount || 0 : 0),
  }));
  const spotIdx = Math.max(0, Math.floor(chartData.length * 0.6));
  const spotPoint = chartData[spotIdx] || chartData[chartData.length - 1];

  return (
    <div className="light-dash rounded-3xl -m-6 md:-m-10 p-6 md:p-10 min-h-[calc(100vh-4rem)]" data-testid="admin-overview">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-4 mb-8">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#B7B0A9]" />
          <input
            data-testid="global-search"
            className="w-full h-11 pl-9 pr-16 rounded-full border border-[#EEEDE8] bg-white text-sm text-[#1F1B1A] placeholder-[#B7B0A9] focus:outline-none focus:ring-2 focus:ring-[#EEE9E3]"
            placeholder="Search..."
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] px-1.5 py-0.5 rounded bg-[#F3F1EC] text-[#8B8680] font-medium">⌘K</span>
        </div>
        <div className="flex items-center gap-3">
          <input type="month" value={selectedMonth} onChange={(e) => onMonthChange(e.target.value)}
            className="h-10 px-3 rounded-full border border-[#EEEDE8] bg-white text-sm text-[#1F1B1A] focus:outline-none focus:ring-2 focus:ring-[#EEE9E3]"
            data-testid="overview-month-select" />
          <button className="w-10 h-10 rounded-full bg-white border border-[#EEEDE8] flex items-center justify-center text-[#8B8680] hover:text-[#1F1B1A]" data-testid="notification-btn">
            <Bell className="w-4 h-4" />
          </button>
          <button className="w-10 h-10 rounded-full bg-white border border-[#EEEDE8] flex items-center justify-center text-[#8B8680] hover:text-[#1F1B1A]">
            <MoreHorizontal className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3 pl-2">
            <div className="w-10 h-10 rounded-full bg-[#3D1F1F] text-white flex items-center justify-center font-medium text-sm">
              {initials(user?.name)}
            </div>
            <div className="text-sm font-medium text-[#1F1B1A] hidden sm:block">
              {user?.name?.split(" ").slice(0, 2).join(" ")}
            </div>
          </div>
        </div>
      </div>

      {/* Hero + KPI cards */}
      <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-end mb-8">
        <div>
          <div className="text-sm text-[#8B8680]">Welcome back,</div>
          <h1 className="font-serif-display italic text-5xl md:text-6xl text-[#1F1B1A] leading-tight mt-1">
            {user?.name || "There"}
          </h1>
        </div>
        <div className="flex flex-wrap gap-4">
          <Kpi label="Appointments" value={kpi.totalAppointments || kpi.appointments_today} delta={kpi.appointmentGrowth ? `+${kpi.appointmentGrowth}%` : ""} icon={Package} />
          <Kpi label="Customers" value={kpi.totalCustomers || kpi.clients_served} icon={Star} />
          <Kpi label="Services" value={kpi.totalServices || kpi.staff_on_duty} icon={Store} />
        </div>
      </div>

      {/* Row 1: Balance stats + Top services */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Balance Statistics */}
        <div className="card p-6" data-testid="balance-statistics-card">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#1F1B1A]">Balance statistics</h3>
            <button className="w-8 h-8 rounded-full bg-[#F5F5F2] flex items-center justify-center text-[#8B8680] hover:text-[#1F1B1A]">
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-4">
            <div className="text-xs text-[#8B8680]">Total revenue</div>
            <div className="flex items-baseline gap-3 mt-1">
              <div className="text-4xl font-semibold text-[#1F1B1A] tracking-tight">{formatINR(rev.total || 0)}</div>
              <div className="text-xs text-emerald-600 font-medium flex items-center gap-0.5"><ArrowUp className="w-3 h-3" /> 0.25%</div>
            </div>
          </div>

          <div className="h-52 mt-6 relative">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#7FD48F" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#7FD48F" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="day" hide />
                  <YAxis hide />
                  <Tooltip
                    contentStyle={{ background: "#fff", border: "1px solid #EEEDE8", borderRadius: 12, fontSize: 12 }}
                    formatter={(v) => formatINR(v)}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#4EBF6A" strokeWidth={2.5} fill="url(#revGrad)" activeDot={{ r: 5, fill: "#4EBF6A" }} />
                  <Line type="monotone" dataKey="expenses" stroke="#E7A0A0" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-[#8B8680]">No revenue data yet.</div>
            )}
            {spotPoint && (
              <div className="absolute right-6 top-0 bg-white border border-[#EEEDE8] shadow-sm rounded-xl px-3 py-2 pointer-events-none">
                <div className="text-[10px] text-[#8B8680]">Revenue</div>
                <div className="flex items-baseline gap-1">
                  <div className="text-sm font-semibold text-[#1F1B1A]">{formatINRCompact(spotPoint?.revenue || 0)}</div>
                  <div className="text-[10px] text-emerald-600 font-medium flex items-center"><ArrowUp className="w-2.5 h-2.5" /> 0.25%</div>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="rounded-2xl p-4 bg-[#E7F6DA]" data-testid="revenue-tile">
              <div className="text-xs text-[#4A6B33] font-medium">Revenue</div>
              <div className="flex items-end justify-between mt-2">
                <div className="text-2xl font-semibold text-[#1F1B1A]">{formatINRCompact(rev.total || 0)}</div>
                <div className="w-7 h-7 rounded-md bg-[#B7E38F] flex items-center justify-center text-[#3B5A22]"><Package className="w-3.5 h-3.5" /></div>
              </div>
              <div className="text-[10px] text-[#4A6B33] mt-1">Today</div>
            </div>
            <div className="rounded-2xl p-4 bg-[#FCE1E1]" data-testid="expenses-tile">
              <div className="text-xs text-[#7A2A2A] font-medium">Expenses</div>
              <div className="flex items-end justify-between mt-2">
                <div className="text-2xl font-semibold text-[#1F1B1A]">{formatINRCompact(rev.totalExpenses || 0)}</div>
                <div className="w-7 h-7 rounded-md bg-[#F2B6B6] flex items-center justify-center text-[#7A2A2A]"><Package className="w-3.5 h-3.5" /></div>
              </div>
              <div className="text-[10px] text-[#7A2A2A] mt-1">Total</div>
            </div>
          </div>
        </div>

        {/* Top Services */}
        <div className="card p-6" data-testid="top-services-card">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#1F1B1A]">Top services</h3>
            <button className="w-8 h-8 rounded-full bg-[#F5F5F2] flex items-center justify-center text-[#8B8680] hover:text-[#1F1B1A]">
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-[1.7fr_1fr_1fr_1fr] text-xs text-[#8B8680] mt-6 mb-2 px-1">
            <div>Services</div>
            <div className="text-right">Avg Price</div>
            <div className="text-right">Bookings</div>
            <div className="text-right">Revenue</div>
          </div>
          <div className="space-y-3">
            {(data.topServices || data.top_services || []).length === 0 && (
              <div className="text-sm text-[#8B8680] py-6 text-center">No service bookings yet.</div>
            )}
            {(data.topServices || data.top_services || []).map((s) => {
              const ic = svcIcon(s.category);
              const Ico = ic.icon;
              return (
                <div key={s.id} className="grid grid-cols-[1.7fr_1fr_1fr_1fr] items-center py-2 px-1" data-testid={`top-service-${s.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: ic.bg, color: ic.fg }}>
                      <Ico className="w-4 h-4" />
                    </div>
                    <div className="text-sm text-[#1F1B1A] font-medium truncate">{s.name}</div>
                  </div>
                  <div className="text-right text-sm text-[#1F1B1A]">{formatINRCompact(s.avgPrice || 0)}</div>
                  <div className="text-right text-sm text-[#1F1B1A]">{s.bookings || s.count}</div>
                  <div className="text-right text-sm text-[#1F1B1A] font-medium">{formatINRCompact(s.revenue)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Row 2: Top Performers (Sales) + Top Performers (Products) */}
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="card p-6" data-testid="top-performer-sales-card">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#1F1B1A]">Top 5 Performers (Sales)</h3>
            <button className="w-8 h-8 rounded-full bg-[#F5F5F2] flex items-center justify-center text-[#8B8680] hover:text-[#1F1B1A]">
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
          {(data.topPerformers || data.top_performers || []).length > 0 ? (
            <div className="mt-4 space-y-3">
              {(data.topPerformers || data.top_performers || []).slice(0, 5).map((p, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-[#FAFAF8] border border-[#EEEDE8]">
                  <div className="w-10 h-10 rounded-full bg-[#3D1F1F] text-white font-medium text-sm flex items-center justify-center shrink-0">
                    {initials(p.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#1F1B1A] truncate">{p.name}</div>
                    <div className="text-xs text-[#8B8680]">{p.servicesCompleted || p.services_completed || 0} services</div>
                  </div>
                  <div className="text-sm font-semibold text-[#1F1B1A]">{formatINRCompact(p.revenue || 0)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-[#8B8680] py-8 text-center">No data available.</div>
          )}
        </div>

        <div className="card p-6" data-testid="top-performer-products-card">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#1F1B1A]">Top 5 Performers (Products)</h3>
            <button className="w-8 h-8 rounded-full bg-[#F5F5F2] flex items-center justify-center text-[#8B8680] hover:text-[#1F1B1A]">
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
          {(data.productPerformers || []).length > 0 ? (
            <div className="mt-4 space-y-3">
              {(data.productPerformers || []).slice(0, 5).map((p, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-[#FAFAF8] border border-[#EEEDE8]">
                  <div className="w-10 h-10 rounded-full bg-[#3D1F1F] text-white font-medium text-sm flex items-center justify-center shrink-0">
                    {initials(p.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#1F1B1A] truncate">{p.name}</div>
                    <div className="text-xs text-[#8B8680]">{p.productsSold || p.products_sold || p.itemsSold || 0} products</div>
                  </div>
                  <div className="text-sm font-semibold text-[#1F1B1A]">{formatINRCompact(p.revenue || 0)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-[#8B8680] py-8 text-center">No data available.</div>
          )}
        </div>
      </div>

      {/* Row 3: Day schedule */}
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <DaySchedule items={data.daySchedule || data.day_schedule} date={data.scheduleDate || data.schedule_date} />
      </div>

      {/* Row 4: Customers (inactive by last service) */}
      <div className="grid grid-cols-1 gap-6 mt-6">
        <InactiveCustomers />
      </div>
    </div>
  );
}

function DaySchedule({ items = [], date }) {
  if (!items || items.length === 0) return null;
  // Group by time
  const grouped = {};
  items.forEach((it) => {
    const hour = (it.time || "00:00").slice(0, 5);
    grouped[hour] = grouped[hour] || [];
    grouped[hour].push(it);
  });
  const hours = Object.keys(grouped).sort();

  const legend = [
    { key: "haircut", label: "Haircut", cls: "bg-[#DDF2C7]" },
    { key: "coloring", label: "Coloring", cls: "bg-[#E5DEFB]" },
    { key: "treatment", label: "Treatment", cls: "bg-[#CFE6F0]" },
  ];

  return (
    <div className="card p-6" data-testid="day-schedule-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-lg font-semibold text-[#1F1B1A]">Day Schedule</h3>
        <div className="flex items-center gap-3 text-xs text-[#4A4643]">
          {legend.map((l) => (
            <div key={l.key} className="flex items-center gap-1.5">
              <span className={`w-2.5 h-2.5 rounded-full ${l.cls}`} /> {l.label}
            </div>
          ))}
          <button className="w-7 h-7 rounded-full bg-[#F5F5F2] flex items-center justify-center text-[#8B8680] hover:text-[#1F1B1A]">
            <ArrowUpRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="text-xs text-[#8B8680] mt-1">{date}</div>
      <div className="mt-5 space-y-3">
        {hours.length === 0 && <div className="text-sm text-[#8B8680] py-8 text-center">No appointments scheduled.</div>}
        {hours.map((h) => (
          <div key={h} className="flex items-start gap-4">
            <div className="text-xs text-[#8B8680] font-medium w-12 pt-1.5">{h}</div>
            <div className="flex-1 flex flex-wrap gap-2">
              {grouped[h].map((it) => (
                <div
                  key={it.id}
                  data-testid={`schedule-item-${it.id}`}
                  className={`${scheduleClass(it.category)} rounded-xl px-3 py-2 text-xs min-w-[140px] max-w-[220px]`}
                >
                  <div className="font-semibold">{it.customer}</div>
                  {it.phone && <div className="opacity-80 text-[11px] mt-0.5">{it.phone}</div>}
                  {!it.phone && <div className="opacity-70 text-[11px] mt-0.5">{it.service}</div>}
                  {it.notes && <div className="opacity-70 text-[11px] mt-0.5 line-clamp-2">{it.notes}</div>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ----- Inactive customers (by days since last service) -----
const INACTIVE_DAY_OPTIONS = [30, 45, 60, 75, 90];

function InactiveCustomers() {
  const [days, setDays] = useState(30);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/analytics/inactive-customers?days=${days}`)
      .then((r) => { if (active) setInfo(r.data); })
      .catch(() => { if (active) setInfo(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [days]);

  const customers = info?.customers || [];

  return (
    <div className="card p-6" data-testid="inactive-customers-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-[#1F1B1A]">Inactive Customers</h3>
          <div className="text-xs text-[#8B8680] mt-0.5">
            No service in the last {days} days
            {info?.cutoffDate ? ` · since ${info.cutoffDate}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!loading && (
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-[#FCE1E1] text-[#7A2A2A]" data-testid="inactive-customers-count">
              {info?.count ?? customers.length} inactive
            </span>
          )}
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="h-9 px-3 rounded-full border border-[#EEEDE8] bg-white text-sm text-[#1F1B1A] focus:outline-none focus:ring-2 focus:ring-[#EEE9E3]"
            data-testid="inactive-customers-days-select"
          >
            {INACTIVE_DAY_OPTIONS.map((d) => (
              <option key={d} value={d}>{d} days</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#8B8680] py-8 text-center">Loading…</div>
      ) : customers.length === 0 ? (
        <div className="text-sm text-[#8B8680] py-8 text-center">No inactive customers in this window.</div>
      ) : (
        <>
          <div className="grid grid-cols-[1.6fr_1.2fr_1.2fr_1fr] text-xs text-[#8B8680] mt-6 mb-2 px-1">
            <div>Customer</div>
            <div>Last Service</div>
            <div>Reminder Sent</div>
            <div className="text-right">Days Since</div>
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {customers.map((c) => (
              <div key={c.id} className="grid grid-cols-[1.6fr_1.2fr_1.2fr_1fr] items-center py-2 px-1 rounded-xl hover:bg-[#FAFAF8]" data-testid={`inactive-customer-${c.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-[#3D1F1F] text-white font-medium text-xs flex items-center justify-center shrink-0">
                    {initials(c.name)}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-[#1F1B1A] font-medium truncate">{c.name || "—"}</div>
                    <div className="text-xs text-[#8B8680] truncate">{c.phone || c.email || "—"}</div>
                  </div>
                </div>
                <div className="text-sm text-[#4A4643]">{c.lastServiceDateFormatted || "Never"}</div>
                <div className="text-sm text-[#4A4643]">{c.lastReminderSentDate ? formatDate(c.lastReminderSentDate) : "—"}</div>
                <div className="text-right text-sm font-medium text-[#1F1B1A]">
                  {c.daysSinceLastService ?? "—"}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ----- Root component -----
export default function Overview() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [appts, setAppts] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  const fetchData = async (month) => {
    try {
      if (norm(user.roleName) !== "customer") {
        try {
          const r = await api.get(`/analytics/overview?month=${month}`);
          setData(r.data);
        } catch {
          setData({});
        }
      }
      const a = await api.get("/appointments");
      setAppts(Array.isArray(a.data) ? a.data : []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    fetchData(selectedMonth);
  }, [user, selectedMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  if (norm(user.roleName) === "customer") return <CustomerView user={user} appts={appts} />;
  if (data === null) return <div className="text-emerald-muted">Loading…</div>;
  if (!data.kpis && !data.revenue) {
    // Fallback: show simple stats from appointments when analytics API isn't available
    const scheduled = appts.filter((a) => norm(a.status) === "scheduled").length;
    const completed = appts.filter((a) => norm(a.status) === "completed").length;
    const totalRevenue = appts.reduce((s, a) => s + Number(a.price || 0), 0);
    return (
      <div className="space-y-10" data-testid="admin-overview">
        <div>
          <div className="overline mb-3">Dashboard</div>
          <h1 className="font-heading text-5xl text-white">Good day, {user.name?.split(" ")[0] || user.firstName || ""}.</h1>
          <p className="text-emerald-muted mt-2">Here's how the house is performing.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6">
            <div className="overline">Revenue</div>
            <div className="font-heading text-4xl text-white mt-3">{formatINR(totalRevenue)}</div>
          </div>
          <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6">
            <div className="overline">Appointments</div>
            <div className="font-heading text-4xl text-white mt-3">{appts.length}</div>
            <div className="text-xs text-emerald-muted mt-2">{scheduled} scheduled</div>
          </div>
          <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6">
            <div className="overline">Completed</div>
            <div className="font-heading text-4xl text-white mt-3">{completed}</div>
          </div>
          <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6">
            <div className="overline">Today</div>
            <div className="font-heading text-4xl text-white mt-3">
              {appts.filter((a) => a.appointmentDate === new Date().toISOString().slice(0, 10)).length}
            </div>
            <div className="text-xs text-emerald-muted mt-2">appointments today</div>
          </div>
        </div>
        <div>
          <h2 className="font-heading text-2xl text-white mb-4">Upcoming Appointments</h2>
          <div className="space-y-3">
            {appts.filter((a) => norm(a.status) === "scheduled").slice(0, 5).map((a) => (
              <div key={a.id} className="bg-emerald-surface border border-gold-subtle rounded-xl p-5 flex justify-between items-center">
                <div>
                  <div className="font-heading text-xl text-white">{a.serviceName}</div>
                  <div className="text-xs text-emerald-muted mt-1">{a.customerName} · {a.staffName} · {formatDate(a.appointmentDate)} at {formatClock(a.startTime)}</div>
                </div>
                <div className="text-gold">{formatINR(a.price)}</div>
              </div>
            ))}
            {appts.filter((a) => norm(a.status) === "scheduled").length === 0 && (
              <p className="text-emerald-muted text-sm">No upcoming appointments.</p>
            )}
          </div>
        </div>
      </div>
    );
  }
  return <AdminDash user={user} data={data} selectedMonth={selectedMonth} onMonthChange={setSelectedMonth} />;
}
